import React, { Component } from 'react';

import { injectIntl, intlShape, FormattedMessage } from '../../../util/reactIntl';
import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';

import { FieldDateRangeController } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import FilterPopupForSidebar from './FilterPopupForSidebar';
import css from './EventDateRangeFilter.module.css';

const SHOW_PAST_EVENTS_PARAM = 'showPastEvents';

const getEventDateQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'eventDateEnd'; // Changed from 'eventDate' to 'eventDateEnd' (LONG schema for range queries)
};

// Parse query parameters from TWO separate params: pub_eventDateStart and pub_eventDateEnd
// These contain timestamps with range notation: ",maxTimestamp" and "minTimestamp,"
const parseValue = (initialValues = {}) => {
  const startParam = initialValues.pub_eventDateStart; // Format: ",endTimestamp"
  const endParam = initialValues.pub_eventDateEnd;     // Format: "startTimestamp,"
  
  if (!startParam && !endParam) {
    return { dates: null };
  }
  
  // Extract timestamps by removing commas
  const endTimestamp = startParam ? parseInt(startParam.replace(',', ''), 10) : null;
  const startTimestamp = endParam ? parseInt(endParam.replace(',', ''), 10) : null;
  
  if (startTimestamp && endTimestamp && !isNaN(startTimestamp) && !isNaN(endTimestamp)) {
    return {
      dates: {
        startDate: new Date(startTimestamp),
        endDate: new Date(endTimestamp),
      },
    };
  }
  
  return { dates: null };
};

// Format dateRange value for the query. It's given by FieldDateRangeController:
// { dates: { startDate, endDate } }
// Now uses TWO query params: pub_eventDateStart and pub_eventDateEnd (LONG schema for range queries)
// If allowPastDates is true, it means "Include past events" is checked - show ALL events with dates
const formatValue = (dateRange, queryParamName, allowPastDates = false) => {
  // When "Include past events" is checked, don't apply date filter at all
  // This will show ALL events in the category, regardless of date
  if (allowPastDates) {
    return {
      pub_eventDateStart: null,
      pub_eventDateEnd: null,
    };
  }
  
  const hasDates = dateRange && dateRange.dates;
  const { startDate, endDate } = hasDates ? dateRange.dates : {};
  
  // Convert to timestamps (milliseconds since epoch) for LONG schema range queries
  const startTimestamp = startDate ? startDate.getTime() : null;
  const endTimestamp = endDate ? endDate.getTime() : null;
  
  // Return range query for both start and end fields
  // Query format: pub_eventDateStart: ",endTimestamp" and pub_eventDateEnd: "startTimestamp,"
  // This matches events where:
  //   - eventDateStart <= user's endDate (event starts before user's range ends)
  //   - eventDateEnd >= user's startDate (event ends after user's range starts)
  if (startTimestamp && endTimestamp) {
    return {
      pub_eventDateStart: `,${endTimestamp}`, // Event must start before or on user's end date
      pub_eventDateEnd: `${startTimestamp},`, // Event must end after or on user's start date
    };
  }
  
  // Clear filters if no dates selected
  return {
    pub_eventDateStart: null,
    pub_eventDateEnd: null,
  };
};

// ShowPastEventsCheckbox component
const ShowPastEventsCheckbox = ({ checked, onChange }) => {
  return (
    <div className={css.showPastEventsWrapper}>
      <label className={css.showPastEventsLabel}>
        <input
          type="checkbox"
          className={css.showPastEventsCheckbox}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
        />
        <FormattedMessage id="EventDateRangeFilter.showPastEvents" />
      </label>
    </div>
  );
};

/**
 * EventDateRangeFilter component
 *
 * Allows filtering events by date range. Converts date range to/from ISO8601 format
 * for the query parameters.
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.id - The ID
 * @param {React.Node} [props.label] - The label
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {boolean} [props.liveEdit] - Whether to live edit
 * @param {Array<string>} [props.queryParamNames] - The query param names (e.g. ['eventDate'])
 * @param {Function} props.onSubmit - The function to submit
 * @param {Object} [props.initialValues] - The initial values
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
export class EventDateRangeFilterComponent extends Component {
  constructor(props) {
    super(props);

    // Parse initial values to check if past dates are selected
    const { initialValues, queryParamNames } = props;
    const eventDateQueryParamName = getEventDateQueryParamName(queryParamNames);
    const parsedInitial = parseValue(initialValues);
    const hasPastDatesSelected = !!parsedInitial.dates && parsedInitial.dates.startDate < new Date();

    this.state = { 
      isOpen: true,
      allowPastDates: hasPastDatesSelected
    };

    this.popupControllerRef = null;
    this.plainControllerRef = null;

    this.toggleIsOpen = this.toggleIsOpen.bind(this);
    this.handleShowPastEventsChange = this.handleShowPastEventsChange.bind(this);
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  handleShowPastEventsChange(checked) {
    this.setState({ allowPastDates: checked }, () => {
      // Re-submit the form with the new allowPastDates setting
      const { onSubmit, queryParamNames, initialValues } = this.props;
      const eventDateQueryParamName = getEventDateQueryParamName(queryParamNames);
      
      // Get current date range from initial values
      const parsedValue = parseValue(initialValues);
      
      // Submit with current dates and new allowPastDates setting
      onSubmit(formatValue(parsedValue, eventDateQueryParamName, checked));
    });
  }

  render() {
    const {
      className,
      rootClassName,
      showAsPopup = true,
      isDesktop = false,
      initialValues,
      id,
      contentPlacementOffset = 0,
      onSubmit,
      queryParamNames,
      label,
      intl,
      ...rest
    } = this.props;

    const { allowPastDates } = this.state;

    const eventDateQueryParamName = getEventDateQueryParamName(queryParamNames);
    const parsedValue = parseValue(initialValues);
    
    const initialDates = { dates: parsedValue.dates };

    const isSelected = !!initialDates.dates;
    const startDate = initialDates.dates ? initialDates.dates.startDate : null;
    const endDate = initialDates.dates ? initialDates.dates.endDate : null;

    const format = {
      month: 'short',
      day: 'numeric',
    };

    const formattedStartDate = isSelected ? intl.formatDate(startDate, format) : null;
    const formattedEndDate = isSelected ? intl.formatDate(endDate, format) : null;

    const labelForPlain = isSelected
      ? intl.formatMessage(
          { id: 'EventDateRangeFilter.labelSelectedPlain' },
          {
            dates: `${formattedStartDate} - ${formattedEndDate}`,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'EventDateRangeFilter.labelPlain' });

    const labelForPopup = isSelected
      ? intl.formatMessage(
          { id: 'EventDateRangeFilter.labelSelectedPopup' },
          {
            dates: `${formattedStartDate} - ${formattedEndDate}`,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'EventDateRangeFilter.labelPopup' });

    const labelSelection = isSelected
      ? intl.formatMessage(
          { id: 'EventDateRangeFilter.labelSelectedPopup' },
          {
            dates: `${formattedStartDate} - ${formattedEndDate}`,
          }
        )
      : null;

    const handleSubmit = values => {
      onSubmit(formatValue(values, eventDateQueryParamName, this.state.allowPastDates));
    };

    const onClearPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onClear: () => this.popupControllerRef.onReset(null, null) }
        : {};

    const onCancelPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onCancel: () => this.popupControllerRef.onReset(startDate, endDate) }
        : {};

    const onClearPlainMaybe =
      this.plainControllerRef && this.plainControllerRef.onReset
        ? { onClear: () => this.plainControllerRef.onReset(null, null) }
        : {};

    // Set minimumDate to today if past dates are not allowed
    const minimumDate = allowPastDates ? null : new Date();

    return showAsPopup ? (
      <FilterPopup
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={labelForPopup}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={0}
          minimumDate={minimumDate}
          controllerRef={node => {
            this.popupControllerRef = node;
          }}
        />
        <ShowPastEventsCheckbox
          checked={allowPastDates}
          onChange={this.handleShowPastEventsChange}
        />
      </FilterPopup>
    ) : isDesktop ? (
      <FilterPopupForSidebar
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={label}
        labelSelection={labelSelection}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={0}
          minimumDate={minimumDate}
          controllerRef={node => {
            this.popupControllerRef = node;
          }}
        />
        <ShowPastEventsCheckbox
          checked={allowPastDates}
          onChange={this.handleShowPastEventsChange}
        />
      </FilterPopupForSidebar>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelection}
        labelSelectionSeparator=":"
        isSelected={isSelected}
        id={`${id}.plain`}
        liveEdit
        onSubmit={handleSubmit}
        {...onClearPlainMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={0}
          minimumDate={minimumDate}
          controllerRef={node => {
            this.plainControllerRef = node;
          }}
        />
        <ShowPastEventsCheckbox
          checked={allowPastDates}
          onChange={this.handleShowPastEventsChange}
        />
      </FilterPlain>
    );
  }
}

const EventDateRangeFilter = injectIntl(EventDateRangeFilterComponent);

export default EventDateRangeFilter;
